import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Popover,
  PopoverArrow,
  PopoverArrowProps,
  PopoverContent,
  PopoverContentProps,
  PopoverProps,
  PopoverTrigger,
  useDisclosure,
  useStyleConfig,
} from '@chakra-ui/react';

export type TooltipVariant = 'light' | 'dark';

export interface TooltipProps extends Omit<PopoverProps, 'children'> {
  /**
   * The content to display in the tooltip.
   * Can be a string or React node.
   */
  label?: React.ReactNode;

  /**
   * The element that triggers the tooltip.
   * Non-interactive elements (like icons) are automatically wrapped in an accessible button.
   */
  children: React.ReactElement;

  /**
   * Visual variant of the tooltip
   * Defaults to theme's defaultProps (currently 'dark')
   */
  variant?: TooltipVariant;

  /**
   * Whether to show an arrow pointing to the trigger
   * @default false
   */
  hasArrow?: boolean;

  /**
   * Additional props passed to the PopoverContent component
   */
  contentProps?: PopoverContentProps;

  /**
   * Additional props passed to the PopoverArrow component
   */
  arrowProps?: PopoverArrowProps;

  /**
   * ARIA label for accessibility.
   * If not provided, will fallback to the child's existing aria-label or the `label` prop (if it's a string).
   */
  'aria-label'?: string;
}

/**
 * A customizable tooltip component built on Chakra UI's Popover.
 * Supports light and dark variants, full accessibility, and performance optimizations.
 *
 * Features:
 * - Uses Chakra UI's Popover with standard popover accessibility semantics
 * - Styles dynamically loaded from UDS Chakra theme for design system consistency
 * - Automatic wrapping of non-interactive elements in accessible buttons
 * - Keyboard focus support for accessibility without overriding existing functionality
 * - Preserves existing onClick handlers and aria-labels for interactive elements
 * - Controlled and uncontrolled state support
 *
 * @example Basic usage with icons (most common pattern)
 * ```tsx
 * <Tooltip label={t('dashboard.weeklyChallengeSection.weeklyChallengeInfo')} hasArrow>
 *   <InfoIcon color="black" height="24px" width="24px" />
 * </Tooltip>
 * // Icon automatically wrapped in accessible button with proper aria-label
 * ```
 *
 * @example With IconButton and light variant
 * ```tsx
 * <Tooltip label={t('program.removeFavorite')} variant="light" hasArrow>
 *   <IconButton
 *     aria-label={t('program.removeFavoriteAriaLabel')}
 *     icon={<HeartIcon />}
 *     variant="solid"
 *   />
 * </Tooltip>
 * ```
 *
 * @example With JSX content and placement
 * ```tsx
 * <Tooltip label={<RowHover points={points} />} placement="bottom" variant="light" hasArrow>
 *   <Box>{points.total > 0 && <CategoryPointsBar max={max} points={points} />}</Box>
 * </Tooltip>
 * ```
 *
 * @example Controlled tooltip
 * ```tsx
 * <Tooltip isOpen={isOpen} label={description} hasArrow>
 *   <IconButton
 *     aria-label={`More information about "${title}".`}
 *     icon={<InfoIcon />}
 *     onClick={onToggle}
 *     onFocus={onOpen}
 *     onBlur={onClose}
 *   />
 * </Tooltip>
 * ```
 */
export const Tooltip = React.memo<TooltipProps>(
  ({
    'aria-label': ariaLabel,
    arrowProps = {},
    children,
    contentProps = {},
    hasArrow = false,
    label,
    variant,
    ...popoverProps
  }) => {
    const { isOpen, onOpen, onClose } = useDisclosure({
      isOpen: popoverProps.isOpen,
      onOpen: popoverProps.onOpen,
      onClose: popoverProps.onClose,
    });

    // Get styles from theme based on variant
    const styles = useStyleConfig('Tooltip', { variant });

    // Click handler for accessibility (keyboard and mouse) - only for non-interactive elements
    const handleClick = useCallback(() => {
      isOpen ? onClose() : onOpen();
    }, [isOpen, onOpen, onClose]);

    // Calculate aria-label consistently: explicit > child's existing > fallback to string label
    const ariaLabelValue = ariaLabel || children.props['aria-label'] || (typeof label === 'string' ? label : undefined);

    // Determine trigger based on child element type and properties
    const childType = children.type;

    // Check if it's a native interactive HTML element
    const isNativeInteractiveElement =
      typeof childType === 'string' &&
      ['button', 'input', 'a', 'textarea', 'select', 'details', 'summary', 'dialog'].includes(childType);

    // Check if it has interactive ARIA role
    const hasInteractiveAriaRole =
      children.props.role &&
      ['button', 'link', 'checkbox', 'radio', 'switch', 'tab', 'menuitem', 'option'].includes(children.props.role);

    // Check if it's contenteditable
    const isContentEditable = children.props.contentEditable === true || children.props.contentEditable === 'true';

    // Check if it has interactive event handlers
    const hasInteractiveHandlers = children.props.onClick || children.props.onFocus;

    // Check if it's focusable via tabIndex (excluding tabIndex={-1} which removes from tab order)
    const isFocusableViaTabIndex = children.props.tabIndex !== undefined && children.props.tabIndex > -1;

    // If element is already interactive in any way, clone with aria-label and click handler
    const isAlreadyInteractive =
      isNativeInteractiveElement ||
      hasInteractiveAriaRole ||
      isContentEditable ||
      hasInteractiveHandlers ||
      isFocusableViaTabIndex;

    // Memoized interactive trigger - always clones to merge controlled state handlers
    // This prevents unnecessary re-renders when the cloning logic doesn't change
    const interactiveTrigger = useMemo(() => {
      if (!isAlreadyInteractive) return null;

      const needsAriaLabelCloning = ariaLabelValue && ariaLabelValue !== children.props['aria-label'];

      const cloneProps: Record<string, any> = {
        onMouseEnter: (e: React.MouseEvent) => {
          children.props.onMouseEnter?.(e);
          onOpen();
        },
        onMouseLeave: (e: React.MouseEvent) => {
          children.props.onMouseLeave?.(e);
          onClose();
        },
        onFocus: (e: React.FocusEvent) => {
          children.props.onFocus?.(e);
          onOpen();
        },
        onBlur: (e: React.FocusEvent) => {
          children.props.onBlur?.(e);
          onClose();
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          children.props.onKeyDown?.(e);
          if (e.key === 'Escape') {
            onClose();
          }
        },
      };

      if (needsAriaLabelCloning) {
        cloneProps['aria-label'] = ariaLabelValue;
      }

      return React.cloneElement(children, cloneProps);
    }, [isAlreadyInteractive, ariaLabelValue, children, onOpen, onClose]);

    // Calculate bridge positioning based on placement
    const getBridgeStyles = useMemo(() => {
      const placement = popoverProps.placement || 'bottom';
      const gutter = popoverProps.gutter || 8; // Default Chakra UI gutter

      // Base styles for the invisible bridge
      const baseStyles = {
        content: '""',
        position: 'absolute' as const,
        bg: 'transparent',
      };

      // Calculate positioning based on placement
      if (placement.startsWith('bottom')) {
        return {
          ...baseStyles,
          top: `-${gutter}px`,
          left: '0',
          right: '0',
          height: `${gutter}px`,
        };
      } else if (placement.startsWith('top')) {
        return {
          ...baseStyles,
          bottom: `-${gutter}px`,
          left: '0',
          right: '0',
          height: `${gutter}px`,
        };
      } else if (placement.startsWith('right')) {
        return {
          ...baseStyles,
          left: `-${gutter}px`,
          top: '0',
          bottom: '0',
          width: `${gutter}px`,
        };
      } else if (placement.startsWith('left')) {
        return {
          ...baseStyles,
          right: `-${gutter}px`,
          top: '0',
          bottom: '0',
          width: `${gutter}px`,
        };
      }

      // Fallback to bottom placement
      return {
        ...baseStyles,
        top: `-${gutter}px`,
        left: '0',
        right: '0',
        height: `${gutter}px`,
      };
    }, [popoverProps.placement, popoverProps.gutter]);

    // Don't render if no content to show
    if (!label) {
      return children;
    }

    const trigger = isAlreadyInteractive ? (
      interactiveTrigger
    ) : (
      // For non-interactive elements (like icons), wrap in a button-like element
      <Box
        alignItems='center'
        aria-label={ariaLabelValue}
        as='button'
        background='transparent'
        border='none'
        display='inline-flex'
        height='fit-content'
        justifyContent='center'
        padding={0}
        width='fit-content'
        onBlur={onClose}
        onClick={handleClick}
        onFocus={onOpen}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        {children}
      </Box>
    );

    return (
      <Popover
        autoFocus={false}
        closeDelay={0}
        isOpen={isOpen}
        openDelay={0}
        placement='bottom'
        returnFocusOnClose={false}
        onClose={onClose}
        onOpen={onOpen}
        {...popoverProps}
      >
        <PopoverTrigger>{trigger}</PopoverTrigger>

        <PopoverContent
          // Creates an invisible bridge between the trigger button and tooltip content
          // This prevents the popover from closing when the mouse moves between them
          _before={getBridgeStyles}
          _focus={{ border: 'none', outline: 'none', boxShadow: 'none' }}
          border='none'
          boxShadow='none'
          height='fit-content'
          sx={styles}
          width='fit-content'
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          {...contentProps}
        >
          {hasArrow && <PopoverArrow bg={styles.bgColor as string} border='0' boxShadow='none' {...arrowProps} />}
          {label}
        </PopoverContent>
      </Popover>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
